<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import { IOrder } from '@/interfaces/IOrder';
  import OrderGroupModal from '../../OrderGroupModal.vue';
  import OrdersService from '@/api/services/orders.service';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useUi } from '@/composition/use-ui';
  import { OrderPaymentStatus, OrderStatus } from '@/enums/OrderEnums';
  import ModalPay from '../../pay/ModalPay.vue';
  import SelectModal from '../../SelectModal.vue';
  import { AlertType } from '@/interfaces/IAlert';
  import { ITable } from '@/interfaces/ITable';
  import ModalPosTableSummary from './ModalPosTableSummary.vue';
  import { deviceCanEverPrint, usePrinting } from '@/composition/use-printing';
  import { PrinterType } from '@/modules/PrintService';
  import { computed } from 'vue';
  import { PaymentType } from '@/enums/PaymentEnums';

  const props = defineProps<{
    table: ITable;
    tableOrders: IOrder[];
    selectedOrders: IOrder[];
  }>();

  const emit = defineEmits(['select-all', 'deselect-all']);

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const printing = usePrinting();

  function selectAllOrders() {
    emit('select-all');
  }

  function deselectAllOrders() {
    emit('deselect-all');
  }

  function openResume() {
    if (!props.selectedOrders.length) return;

    modals.show(
      OrderGroupModal,
      {
        title: 'Resumen de pedidos',
        orders: props.selectedOrders,
        reloadItems: false,
      },
      {
        adaptive: true,
        height: 'auto',
        width: 550,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      }
    );
  }

  async function setOrderStatus(status: OrderStatus) {
    if (!props.selectedOrders.length) return;

    try {
      await OrdersService.massSetStatus(
        props.selectedOrders.map((o) => o.id),
        status,
        restaurantStore.caja?.id
      );
    } catch (error) {
      ui.serverError(error, 'Error al actualizar el estado');
    }
  }

  async function doPayment() {
    const orders = props.selectedOrders.filter(
      (o) => o.paymentStatus !== OrderPaymentStatus.Paid
    );

    modals.show(
      ModalPay,
      {
        //orders,
        type: PaymentType.Order,
        unpaidAmount: orders.reduce(
          (acc, order) => acc + (order.total - order.totalPaid),
          0
        ),
        entityIds: orders.map((o) => o.id),
      },
      {
        name: 'order-pay',
        //classes: 'dialog--height-limit',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      },
      {}
    );
  }

  async function massMove() {
    const orders = props.selectedOrders;

    if (!orders.length) return;
    const fromTable = orders[0].table;

    try {
      const tables = restaurantStore.tables.filter(
        (t) => t.id !== fromTable.id
      );

      modals.show(
        SelectModal,
        {
          title: `Mover ${orders.length} pedidos de la mesa ${fromTable.name}`,
          message:
            'Seleccione la mesa a la que desea mover los pedidos seleccionados',
          placeholder: 'Seleccione la nueva mesa',
          items: tables.map((t) => ({
            value: t.id,
            text: t.name,
          })),
        },
        {
          name: 'move',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          submit: async (toTableId: number) => {
            if (toTableId === fromTable.id) {
              ui.showToast(
                AlertType.Error,
                'No se puede mover a la misma mesa'
              );
              return;
            }
            try {
              await OrdersService.massMove(
                orders.map((o) => o.id),
                toTableId
              );

              ui.showToast(AlertType.Success, 'Pedidos movidos correctamente');
              modals.hide('move');
            } catch (e) {
              ui.serverError(e, 'Error al mover pedidos');
            }
          },
        }
      );
    } catch (e) {
      ui.serverError(e, 'Error al mover pedidos');
    }
  }

  // Ticket, Comanda and Etiquetas
  const printButtons = [
    {
      title: 'Ticket no fiscal',
      icon: 'mdi-ticket-percent',
      click: () => {
        printing
          .printGroup(
            PrinterType.Ticket,
            `pedidos mesa ${props.table.name}`,
            props.selectedOrders
          )
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir ticket');
          });
      },
    },
    {
      title: 'Comanda',
      icon: 'mdi-receipt',
      click: () => {
        printing
          .printGroup(
            PrinterType.Comanda,
            `pedidos mesa ${props.table.name}`,
            props.selectedOrders
          )
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir comanda');
          });
      },
    },
    {
      title: 'Etiquetas',
      icon: 'mdi-tag-text',
      click: () => {
        printing
          .printGroup(
            PrinterType.Etiqueta,
            `pedidos mesa ${props.table.name}`,
            props.selectedOrders
          )
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir etiquetas');
          });
      },
    },
    {
      title: 'Todo',
      icon: 'mdi-printer',
      click: () => {
        printing
          .printGroup(
            PrinterType.Ticket,
            `pedidos mesa ${props.table.name}`,
            props.selectedOrders
          )
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir ticket');
          });
        printing
          .printGroup(
            PrinterType.Comanda,
            `pedidos mesa ${props.table.name}`,
            props.selectedOrders
          )
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir comanda');
          });
        printing
          .printGroup(
            PrinterType.Etiqueta,
            `pedidos mesa ${props.table.name}`,
            props.selectedOrders
          )
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir etiquetas');
          });
      },
    },
  ];

  const allStatus = computed(() => {
    const status = {
      finished: true,
      paid: true,
    };

    for (const order of props.selectedOrders) {
      if (order.status !== OrderStatus.Finished) {
        status.finished = false;

        if (status.paid === false) {
          break;
        }
      }
      if (order.paymentStatus !== OrderPaymentStatus.Paid) {
        status.paid = false;

        if (status.finished === false) {
          break;
        }
      }
    }

    return status;
  });
</script>

<template>
  <div class="footer">
    <div class="footer-selection-container">
      <div class="selection-actions">
        <v-btn
          small
          color="primary"
          @click="selectAllOrders"
          :disabled="selectedOrders.length === tableOrders.length"
        >
          <v-icon small>mdi-checkbox-multiple-marked</v-icon>
        </v-btn>
        <v-btn
          small
          color="gray"
          @click="deselectAllOrders"
          :disabled="selectedOrders.length === 0"
        >
          <v-icon small>mdi-checkbox-blank-off-outline</v-icon>
        </v-btn>
      </div>
      <div class="table-actions">
        <v-menu v-if="deviceCanEverPrint" offset-y top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="action-button"
              color="grey"
              depressed
              dark
              small
              :disabled="selectedOrders.length === 0"
              v-on="on"
              v-bind="attrs"
            >
              <v-icon small left>mdi-printer</v-icon>
              Imprimir
              <v-icon small right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="button in printButtons"
              :key="button.title"
              @click="button.click"
            >
              <v-list-item-icon>
                <v-icon>{{ button.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ button.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          small
          color="info"
          :disabled="selectedOrders.length === 0"
          class="action-btn"
          @click="openResume"
        >
          <v-icon small left>mdi-clipboard-text</v-icon>
          Resumen
        </v-btn>

        <v-btn
          small
          color="primary"
          :disabled="selectedOrders.length === 0 || allStatus.finished"
          class="action-btn"
          @click="setOrderStatus(OrderStatus.Finished)"
        >
          <v-icon small left>mdi-check</v-icon>
          Entregado
        </v-btn>

        <v-btn
          small
          color="success"
          :disabled="selectedOrders.length === 0 || allStatus.paid"
          class="action-btn"
          @click="doPayment"
        >
          <v-icon small left>mdi-cash-register</v-icon>
          Pagar
        </v-btn>

        <v-btn
          small
          color="grey"
          dark
          :disabled="selectedOrders.length === 0"
          class="action-btn"
          @click="massMove"
        >
          <v-icon small left>mdi-table-arrow-right</v-icon>
          Mover
        </v-btn>
      </div>
    </div>
    <ModalPosTableSummary
      :table="table"
      :orders="tableOrders"
      :selected-orders="selectedOrders"
    />
  </div>
</template>

<style scoped lang="scss">
  .footer {
    @apply tw-flex-none tw-divide-y tw-divide-gray-300 tw-divide-dashed;

    .footer-selection-container {
      @apply tw-px-2 tw-py-2 tw-flex tw-items-center tw-space-x-2 tw-justify-between;

      .selection-actions {
        @apply tw-flex tw-space-x-2;
      }

      .table-actions {
        @apply tw-flex tw-gap-2 tw-flex-wrap tw-justify-end;

        .action-btn {
          @apply tw-min-w-[80px];
        }
      }
    }
  }
</style>
